import React, { useEffect, useState, useMemo } from "react";
import { FormService, FormField } from "@shared/services/FormServices";
import { DynamicForm } from "@shared/components/DynamicForm";
import { GenericSelect } from "@shared/components/Select";
import { Controller, UseFormReturn } from "react-hook-form";
import { FIELD_CLASSES } from "@shared/utils/constants";
import { PageHeader } from "@shared/components/PageHeader";
import { Droplets, Zap, Flame, Box } from "lucide-react";
import { Button } from "@shared/ui/button";
import { useParams } from "react-router-dom";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import {
  useUtilityServices,
  useGetRate,
  useUpdatePlan,
  useGetPlanDetailByTab,
} from "../../../hooks";

interface SelectRatesProps {
  data?: any;
  onDataChange?: (data: any) => void;
  onNext?: () => void;
  onPrevious?: () => void;
  isFirstStep?: boolean;
  isLastStep?: boolean;
}

const getServiceIcon = (service: string) => {
  const normalized = service.toLowerCase();
  if (normalized === "water")
    return <Droplets className="text-blue-500 w-5 h-5" />;
  if (normalized === "electricity")
    return <Zap className="text-orange-500 w-5 h-5" />;
  if (normalized === "gas") return <Flame className="text-red-500 w-5 h-5" />;
  return <Box className="text-gray-500 w-5 h-5" />;
};

interface TariffSelectProps {
  name: string;
  placeholder: string;
  tariffs: { label: string; value: any; rateAmount: number }[];
  form: UseFormReturn<any>;
}

const TariffSelect: React.FC<TariffSelectProps> = ({
  name,
  placeholder,
  tariffs,
  form,
}) => (
  <Controller
    name={name}
    control={form.control}
    render={({ field }) => (
      <GenericSelect
        value={field.value || ""}
        onChange={(val) => {
          field.onChange(val);
          const selectedTariff = tariffs.find(
            (t) => String(t.value) === String(val),
          );
          if (selectedTariff) {
            form.setValue(name + "Name", selectedTariff.label);
            form.setValue(name + "Amount", selectedTariff.rateAmount);
          }
        }}
        options={tariffs}
        placeholder={placeholder}
        isSearchable={true}
        className="w-full text-sm"
      />
    )}
  />
);

const RateCard = ({
  service,
  form,
  utilityUnit,
}: {
  service: string;
  form: UseFormReturn<any>;
  utilityUnit?: string;
}) => {
  const [isConditional, setIsConditional] = useState(false);
  const thresholdValue = form.watch(`rates.${service}.threshold`) || "15";

  // Fetch tariffs for the specific service
  const { data: rateResponse } = useGetRate(service);

  const tariffs = useMemo(() => {
    const rateData = rateResponse?.result || rateResponse;
    return Array.isArray(rateData)
      ? rateData.map((rate: any) => ({
          label: rate.rateName,
          value: rate.id || rate.rateId,
          rateAmount:
            rate.rate ||
            rate.amount ||
            rate.chargeRate ||
            rate.charge_rate ||
            0,
        }))
      : [];
  }, [rateResponse]);

  // When edit mode populates tariffBelow/tariffAbove, automatically flip into conditional mode visually
  const tariffBelow = form.watch(`rates.${service}.tariffBelow`);
  const tariffAbove = form.watch(`rates.${service}.tariffAbove`);

  useEffect(() => {
    if (tariffBelow || tariffAbove) {
      setIsConditional(true);
    }
  }, [tariffBelow, tariffAbove]);

  return (
    <div className="border border-gray-200 rounded-xl p-4 bg-white shadow-sm">
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-blue-50 rounded-lg">
            {getServiceIcon(service)}
          </div>
          <div>
            <h4 className="font-semibold text-gray-900 capitalize">
              {service}
            </h4>
            <p className="text-xs text-gray-500">
              {tariffs.length} tariffs available
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => setIsConditional(!isConditional)}
          className={`px-3 py-1.5 text-sm border rounded-lg flex items-center transition-colors ${
            isConditional
              ? "bg-blue-50 border-blue-200 text-blue-800 font-medium"
              : "text-gray-600 border-gray-300 hover:bg-gray-50"
          }`}
        >
          {isConditional ? "Conditional ✓" : "+ Conditional rate"}
        </button>
      </div>

      {!isConditional ? (
        <div className="space-y-4">
          <TariffSelect
            name={`rates.${service}.tariff`}
            placeholder={`Choose a ${service} tariff...`}
            tariffs={tariffs}
            form={form}
          />
          <div className="flex items-center space-x-3 text-sm text-gray-600">
            <span className="font-medium text-xs text-gray-500 ">
              Avg Consumption
            </span>
            <div className="flex items-center">
              <input
                type="number"
                placeholder="e.g. 30"
                className="border border-gray-300 rounded-lg py-1.5 px-3 w-28 shadow-sm text-sm focus:ring-2 focus:ring-blue-100 focus:border-blue-500 outline-none transition-all"
                {...form.register(`rates.${service}.avgConsumption`)}
              />
              <span className="ml-2 text-gray-500 text-xs font-medium">
                {utilityUnit || "NA"}
              </span>
            </div>
            <span className="text-gray-400 text-xs">
              — used for bill projection & financial impact
            </span>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="flex items-center space-x-3 text-sm text-gray-600 bg-gray-50 p-3 rounded-lg border border-gray-200">
            <span className="font-medium text-gray-700">Threshold:</span>
            <div className="relative flex items-center">
              <input
                type="number"
                placeholder="15"
                className="border border-gray-300 rounded-lg p-1.5 w-20 shadow-sm text-sm z-10"
                {...form.register(`rates.${service}.threshold`)}
              />
              <span className="ml-2 text-gray-500 text-sm">units</span>
            </div>
            <span className="text-gray-400 text-sm ml-2">
              — Below uses one tariff, at/above uses another.
            </span>
          </div>

          <div className="flex items-center space-x-4">
            <span className="w-20 text-sm font-medium text-right text-gray-700">
              &lt; {thresholdValue} units
            </span>
            <div className="flex-1">
              <TariffSelect
                name={`rates.${service}.tariffBelow`}
                placeholder={`Rate below ${thresholdValue} units...`}
                tariffs={tariffs}
                form={form}
              />
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <span className="w-20 text-sm font-medium text-right text-gray-700">
              &ge; {thresholdValue} units
            </span>
            <div className="flex-1">
              <TariffSelect
                name={`rates.${service}.tariffAbove`}
                placeholder={`Rate at/above ${thresholdValue} units...`}
                tariffs={tariffs}
                form={form}
              />
            </div>
          </div>
          <div className="flex items-center space-x-3 text-sm text-gray-600 pt-2 border-t border-gray-100">
            <span className="font-medium text-xs text-gray-500 ">
              Avg Consumption
            </span>
            <div className="flex items-center">
              <input
                type="number"
                placeholder="e.g. 30"
                className="border border-gray-300 rounded-lg py-1.5 px-3 w-28 shadow-sm text-sm focus:ring-2 focus:ring-blue-100 focus:border-blue-500 outline-none transition-all"
                {...form.register(`rates.${service}.avgConsumption`)}
              />
              <span className="ml-2 text-gray-500 text-xs font-medium">
                {utilityUnit || "NA"}
              </span>
            </div>
            <span className="text-gray-400 text-xs">
              — used for bill projection & financial impact
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

const CustomRatesRender = (
  form: UseFormReturn<any>,
  utilityUnitsMap: Record<string, string>,
) => {
  const services = form.watch("services") || [];
  const validServices = Array.isArray(services) ? services : [];
  const rates = form.watch("rates") || {};

  const serviceNames = validServices.map((s: any) =>
    typeof s === "object" ? s.value : s,
  );

  const assignedCount = serviceNames.filter((serviceName: string) => {
    const rateData = rates[serviceName];
    return (
      rateData &&
      (rateData.tariff || (rateData.tariffBelow && rateData.tariffAbove))
    );
  }).length;

  return (
    <div className="mt-4">
      <p className="text-xs text-gray-500 mb-6">
        {serviceNames.length} services selected. Pick a rate for each below.
      </p>
      {serviceNames.length === 0 ? (
        <div className="p-8 text-center bg-gray-50 border border-dashed border-gray-200 rounded-xl text-gray-500 text-sm mb-4">
          No utility services selected yet. Pick at least one above to start
          choosing rates.
        </div>
      ) : (
        <div className="space-y-4 mb-4">
          {serviceNames.map((serviceName: string) => (
            <RateCard
              key={serviceName}
              service={serviceName}
              form={form}
              utilityUnit={utilityUnitsMap[serviceName]}
            />
          ))}
        </div>
      )}
      <div className="text-xs text-gray-500 mt-2">
        {assignedCount} of {serviceNames.length} services have a rate assigned.
      </div>
    </div>
  );
};

export const SelectRates: React.FC<SelectRatesProps> = ({
  data,
  onDataChange,
  onNext,
  onPrevious,
  isFirstStep,
  isLastStep,
}) => {
  const { action, planId } = useParams();
  const [hasInitialData] = useState(() => Object.keys(data || {}).length > 0);
  const queryPlanId = hasInitialData ? undefined : planId;
  const remoteUtilityId = useRemoteUtilityId();
  const { mutateAsync: updatePlan } = useUpdatePlan();
  const isEdit = planId && planId !== "create";
  const { data: planDetailResponse } = useGetPlanDetailByTab(
    queryPlanId,
    "rates",
  );
  const { data: utilityServiceData } = useUtilityServices();

  const utilityOptions = useMemo(() => {
    return (
      utilityServiceData?.result
        ?.filter((item: any) => item.isActive)
        ?.map((item: any) => ({ label: item.name, value: item.name })) || []
    );
  }, [utilityServiceData]);

  const utilityUnitsMap = useMemo(() => {
    const map: Record<string, string> = {};
    utilityServiceData?.result?.forEach((item: any) => {
      map[item.name] = item.utilityUnit || "";
    });
    return map;
  }, [utilityServiceData]);

  const selectRatesFields: FormField[] = useMemo(
    () => [
      {
        name: "services",
        label: "Applicable Utility Services",
        type: "multi-select",
        required: true,
        placeholder: "Select services...",
        options: utilityOptions,
        classes: FIELD_CLASSES,
        className: "w-full md:w-1/2 lg:w-1/3",
      },
      {
        name: "ratesCustom",
        type: "custom",
        customRender: (_, form) => CustomRatesRender(form, utilityUnitsMap),
      },
    ],
    [utilityOptions, utilityUnitsMap],
  );

  const form = FormService.createForm({
    fields: selectRatesFields,
    defaultValues: data || {},
    mode: "onChange",
  });

  const isLocalStorageRestored = React.useRef(false);

  useEffect(() => {
    if (hasInitialData && data && !isLocalStorageRestored.current) {
      FormService.populateForm(form, data, selectRatesFields);
      if (data.rates) {
        form.setValue("rates", data.rates);
      }
      isLocalStorageRestored.current = true;
    }
  }, [hasInitialData, data, form, selectRatesFields]);

  const isPopulated = React.useRef(false);

  // Effect to handle population of Edit data
  useEffect(() => {
    if (hasInitialData) return;
    if (isPopulated.current) return;
    if (planDetailResponse?.result) {
      isPopulated.current = true;
      const result = planDetailResponse.result;
      console.log("services are", result.ratesByService);
      const services: any[] = [];
      const rates: Record<string, any> = {};

      if (result.ratesByService) {
        Object.entries(result.ratesByService).forEach(
          ([serviceKey, rateObj]: [string, any]) => {
            const serviceName = serviceKey
              .split(" ")
              .map(
                (w: string) =>
                  w.charAt(0).toUpperCase() + w.slice(1).toLowerCase(),
              )
              .join(" ");
            services.push(serviceName);
            const tariffId =
              rateObj.rateId?.toString() ||
              rateObj.id?.toString() ||
              (typeof rateObj.rate === "object"
                ? rateObj.rate?.id?.toString() || rateObj.rate?.rateId?.toString()
                : rateObj.rate?.toString());

            rates[serviceName] = {
              tariff: tariffId,
              tariffName: rateObj.rateName || rateObj.name,
              rateAmount:
                rateObj.rateAmount || rateObj.tariffAmount || rateObj.amount || 0,
              avgConsumption: rateObj.avgConsumption,
            };
          },
        );
      }

      if (result.conditionalRateRules) {
        Object.entries(result.conditionalRateRules).forEach(
          ([serviceKey, rule]: [string, any]) => {
            const serviceName = serviceKey
              .split(" ")
              .map(
                (w: string) =>
                  w.charAt(0).toUpperCase() + w.slice(1).toLowerCase(),
              )
              .join(" ");

            if (!services.includes(serviceName)) {
              services.push(serviceName);
            }

            const belowId =
              rule.belowRate?.rateId?.toString() ||
              rule.belowRate?.id?.toString() ||
              (typeof rule.belowRate === "object"
                ? rule.belowRate?.id?.toString()
                : rule.belowRate?.toString());

            const aboveId =
              rule.aboveRate?.rateId?.toString() ||
              rule.aboveRate?.id?.toString() ||
              (typeof rule.aboveRate === "object"
                ? rule.aboveRate?.id?.toString()
                : rule.aboveRate?.toString());

            rates[serviceName] = {
              ...rates[serviceName],
              threshold: rule.threshold,
              tariffBelow: belowId,
              tariffBelowName: rule.belowRate?.rateName || rule.belowRate?.name,
              tariffAbove: aboveId,
              tariffAboveName: rule.aboveRate?.rateName || rule.aboveRate?.name,
              avgConsumption: rule.avgConsumption,
            };
          },
        );
      }

      // Use FormService to dynamically populate standard fields based on basicInfo reference
      FormService.populateForm(form, { services }, selectRatesFields);
      // Explicitly set nested dynamic fields since they bypass the flat schema
      form.setValue("rates", rates);
    }
  }, [hasInitialData, planDetailResponse?.result, form, selectRatesFields]);

  const { handleSubmit } = form;

  const onDataChangeRef = React.useRef(onDataChange);
  const dataRef = React.useRef(data);

  useEffect(() => {
    onDataChangeRef.current = onDataChange;
    dataRef.current = data;
  }, [onDataChange, data]);

  useEffect(() => {
    if (!onDataChangeRef.current) return;
    // Emit immediately on mount so Review has data even if the user never touches this step
    onDataChangeRef.current({ ...dataRef.current, ...form.getValues() });
    const subscription = form.watch((value) => {
      onDataChangeRef.current?.({ ...dataRef.current, ...value });
    });
    return () => subscription.unsubscribe();
  }, [form]);

  const onSubmitForm = async (formData: any) => {
    console.log("form data", formData.services);
    let selectedServices: string[] = [];
    if (Array.isArray(formData.services)) {
      selectedServices = formData.services.map((s: any) =>
        typeof s === "object" ? s.value : s,
      );
    } else if (typeof formData.services === "string") {
      selectedServices = formData.services.split(",").filter(Boolean);
    } else if (formData.services) {
      selectedServices = [String(formData.services)];
    }

    // Since the 'rates' fields are dynamically registered inside a custom component,
    // the zod resolver schema strips them out of formData.
    // We must get them directly from the raw form state.
    const rawRates = form.getValues("rates") || {};

    const selected_tariffs: Record<string, any> = {};
    const tariff_selection_rules: Record<string, any> = {};

    selectedServices.forEach((service: string) => {
      const rateData = rawRates[service];
      if (!rateData) return;

      if (rateData.tariffBelow && rateData.tariffAbove) {
        const utilityObj = utilityServiceData?.result?.find(
          (u: any) => String(u.name) === String(service),
        );
        tariff_selection_rules[service] = {
          enabled: true,
          threshold: rateData.threshold,
          threshold_unit: utilityObj?.utilityUnit ?? "NA",
          below_rate_id: rateData.tariffBelow,
          above_rate_id: rateData.tariffAbove,
          avg_consumption: rateData.avgConsumption,
        };
      } else if (rateData.tariff) {
        selected_tariffs[service] = {
          rate_id: rateData.tariff,
          avg_consumption: rateData.avgConsumption,
        };
      }
    });

    const payload = {
      current_step: 3,
      remote_utility_id: remoteUtilityId,
      tariff_config: {
        services: selectedServices,
        selected_tariffs,
        tariff_selection_rules,
      },
    };

    const unmeteredLinkedCharges: any[] = [];
    selectedServices.forEach((service: string) => {
      const utilityObj = utilityServiceData?.result?.find(
        (u: any) => String(u.name) === String(service),
      );
      if (utilityObj && utilityObj.isMeterRequired === false) {
        const rateData = rawRates[service];
        if (rateData) {
          unmeteredLinkedCharges.push({
            name: service,
            isUnmetered: true,
            chargeUnit:
              rateData.tariffName ||
              rateData.tariffBelowName ||
              rateData.tariffAboveName ||
              rateData.tariff ||
              rateData.tariffBelow ||
              rateData.tariffAbove ||
              "",
            rateAmount:
              rateData.tariffAmount ||
              rateData.tariffBelowAmount ||
              rateData.tariffAboveAmount ||
              0,
          });
        }
      }
    });

    try {
      if (planId && planId !== "create") {
        if (action !== "edit") {
          await updatePlan({ planId, payload });
        }
      }
      onDataChange?.({ ...formData, unmeteredLinkedCharges });
      onNext?.();
    } catch (error) {
      console.error("Failed to update plan rates:", error);
    }
  };

  return (
    <div className="">
      <div className="mb-6">
        <PageHeader
          title="Select Rates"
          subtitle="Pick the utility services this plan covers, then choose a rate for each."
        />
      </div>
      <DynamicForm
        fields={selectRatesFields}
        form={form}
        gridColumns={1}
        config={{
          showSubmitButton: false,
          showResetButton: false,
        }}
      />

      <div className="flex items-center justify-between mt-6 border-t pt-6">
        <div>
          {!isFirstStep && onPrevious && (
            <Button type="button" variant="outline" onClick={onPrevious}>
              Previous
            </Button>
          )}
        </div>
        <Button type="button" onClick={handleSubmit(onSubmitForm)}>
          {isLastStep ? "Finish" : "Next"}
        </Button>
      </div>
    </div>
  );
};

export default SelectRates;
